"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 153: Bandai FCG / LZ93D50 with 8 KB WRAM
 * ------------------------------------------------------------------ *
 * Famicom Jump II uses the same LZ93D50 ASIC as the mapper 16 boards,
 * but with two changes that make it a separate board:
 *
 *   - the serial EEPROM is replaced by 8 KB of battery-backed WRAM at
 *     $6000-$7FFF, enabled by bit 5 of $800D;
 *   - the PRG-ROM is 512 KB, split into two 256 KB outer blocks. The
 *     low bit written to $8000-$8007 selects the block, and $8008 only
 *     selects the 16 KB bank inside it. The bank at $C000 is the last
 *     bank of the selected outer block, not simply the last bank of the
 *     whole ROM.
 *
 * The mirroring and IRQ registers are the LZ93D50 ones: $8009 selects
 * the name-table layout, $800A enables the counter (and reloads it from
 * the latch), $800B/$800C hold the latch. The counter runs on CPU
 * cycles, like the mapper 16 LZ93D50 path.
 *
 * Reference: https://www.nesdev.org/wiki/INES_Mapper_153
 * ------------------------------------------------------------------ */

function register(Mappers) {
  Mappers[153] = function (nes) {
    // The LZ93D50 register layout, IRQ counter and mirroring helpers are
    // shared with mapper 16. The EEPROM it allocates stays unused here.
    Mappers[16].call(this, nes);
    this.wram = new Uint8Array(0x2000);
  };

  Mappers[153].prototype = new Mappers[16]();

  Mappers[153].prototype.reset = function () {
    Mappers[16].prototype.reset.apply(this);

    // Outer PRG block and the 16 KB bank inside it. $800D starts with
    // the WRAM disabled, as on the board after a reset.
    this.prgOuter = 0;
    this.prgBank = 0;
    this.ramEnabled = false;

    // Mapper 153 only has the LZ93D50 register window, so the IRQ
    // counter always uses the latched mode.
    this.irqLatchMode = true;
  };

  Mappers[153].prototype.load = function (address) {
    address &= 0xffff;

    if (address >= 0x6000 && address < 0x8000) {
      if (this.ramEnabled) {
        return this.wram[address - 0x6000];
      }
      // No RAM selected: the bus floats. Reading cpu.mem keeps the same
      // open-bus behaviour as the rest of the emulator.
      return this.nes.cpu.mem[address] || 0;
    }

    return Mappers[0].prototype.load.apply(this, arguments);
  };

  Mappers[153].prototype.write = function (address, value) {
    address &= 0xffff;

    if (address >= 0x6000 && address < 0x8000) {
      if (this.ramEnabled) {
        var offset = address - 0x6000;
        this.wram[offset] = value & 0xff;
        this.saveWramByte(offset, value & 0xff);
      }
      // With WRAM disabled the write goes nowhere, exactly like a board
      // with no RAM chip selected.
      return;
    }

    if (address >= 0x8000) {
      this.writeRegister(address & 0x0f, value, true);
      return;
    }

    Mappers[0].prototype.write.apply(this, arguments);
  };

  Mappers[153].prototype.writeRegister = function (reg, value, lzWindow) {
    if (reg <= 0x07) {
      // $8000-$8007: the low bit of every register is the same outer
      // block bit. The game writes the value to all of them; OR-ing the
      // bits reproduces the board's single PRG A18 line.
      this.chrBanks[reg] = value & 0xff;
      this.applyOuterBank();
      return;
    }

    if (reg === 0x08) {
      // $8008: inner 16 KB bank, low four bits only.
      this.prgBank = value & 0x0f;
      this.applyPrgBanks();
      return;
    }

    if (reg === 0x0d) {
      // $800D: bit 5 enables the 8 KB WRAM at $6000-$7FFF.
      this.ramEnabled = (value & 0x20) !== 0;
      return;
    }

    // $8009-$800C are the LZ93D50 mirroring and IRQ registers and are
    // identical to mapper 16.
    if (reg >= 0x09 && reg <= 0x0c) {
      Mappers[16].prototype.writeRegister.call(this, reg, value, lzWindow);
    }
  };

  Mappers[153].prototype.applyOuterBank = function () {
    var outer = 0;
    for (var i = 0; i < 8; i++) {
      outer |= this.chrBanks[i] & 0x01;
    }
    this.prgOuter = outer;
    this.applyPrgBanks();
  };

  Mappers[153].prototype.applyPrgBanks = function () {
    // One outer block is sixteen 16 KB banks (256 KB). The fixed window
    // is the last bank of that block, which is what the reset vectors
    // and the interrupt handlers live in.
    var base = this.prgOuter << 4;
    this.loadRomBank(base + this.prgBank, 0x8000);
    this.loadRomBank(base + 0x0f, 0xc000);
  };

  Mappers[153].prototype.applyBanks = function () {
    this.applyPrgBanks();
    // CHR is 8 KB of RAM owned by the PPU; there are no CHR banks.
  };

  Mappers[153].prototype.saveWramByte = function (offset, value) {
    var ram = this.nes.rom.batteryRamData;
    if (ram !== null && ram !== undefined && offset < ram.length) {
      ram[offset] = value;
    }
    if (typeof this.nes.opts.onBatteryRamWrite === "function") {
      this.nes.opts.onBatteryRamWrite(0x6000 + offset, value);
    }
  };

  Mappers[153].prototype.loadBatteryRam = function () {
    var ram = this.nes.rom.batteryRamData;
    if (ram === null || ram === undefined) {
      return;
    }
    var count = Math.min(this.wram.length, ram.length);
    for (var i = 0; i < count; i++) {
      this.wram[i] = ram[i];
    }
  };

  Mappers[153].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("Bandai FCG 153: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadBatteryRam();
    this.loadTrainer();

    // Power-on: outer block 0, inner bank 0, fixed bank $0F, and WRAM
    // disabled until the game writes $800D.
    this.applyPrgBanks();
    this.applyMirroring();
    this.resetIrqHandler();
  };

  Mappers[153].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.prgOuter = this.prgOuter;
    s.prgBank = this.prgBank;
    s.mirroring = this.mirroring;
    s.ramEnabled = this.ramEnabled;
    s.irqEnabled = this.irqEnabled;
    s.irqCounter = this.irqCounter;
    s.irqLatch = this.irqLatch;
    s.irqAsserted = this.irqAsserted;
    s.irqLatchMode = this.irqLatchMode;
    s.wram = Array.from(this.wram);
    return s;
  };

  Mappers[153].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);

    this.prgOuter = s.prgOuter ? 1 : 0;
    this.prgBank = s.prgBank || 0;
    this.mirroring = s.mirroring || 0;
    this.ramEnabled = s.ramEnabled === true;
    this.irqEnabled = s.irqEnabled === true;
    this.irqCounter = s.irqCounter || 0;
    this.irqLatch = s.irqLatch || 0;
    this.irqAsserted = s.irqAsserted === true;
    this.irqLatchMode = true;

    if (s.wram) {
      for (var i = 0; i < this.wram.length; i++) {
        this.wram[i] = s.wram[i] || 0;
      }
    }

    this.applyPrgBanks();
    this.applyMirroring();
  };
}

module.exports = { register: register };
